/**
 * AppsFlyer read hooks. Thin `useQuery` wrappers over the queries factory;
 * consumers read `.data` / `.isLoading` / `.error` (typed ApiError).
 */
import { useQuery } from '@tanstack/react-query';
import { createAppsflyerQueriesApi } from '../api/appsflyerQueries';
import { appsflyerKeys } from '../queryKeys';

const api = createAppsflyerQueriesApi();

export function useAppsflyerStatus(brandId: string) {
  return useQuery({
    queryKey: appsflyerKeys.status(brandId),
    queryFn: () => api.getStatus(brandId),
    enabled: !!brandId,
  });
}

export function useAppsflyerApps(brandId: string, enabled = true) {
  return useQuery({
    queryKey: appsflyerKeys.apps(brandId),
    queryFn: () => api.listApps(brandId),
    enabled: !!brandId && enabled,
  });
}

export function useAppsflyerEvents(brandId: string, appId: string, enabled = true) {
  return useQuery({
    queryKey: appsflyerKeys.events(brandId, appId),
    queryFn: () => api.listEvents(brandId, appId),
    enabled: !!brandId && !!appId && enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useAppsflyerTemplates(brandId: string, enabled = true) {
  return useQuery({
    queryKey: appsflyerKeys.templates(brandId),
    queryFn: () => api.listTemplates(brandId),
    enabled: !!brandId && enabled,
    staleTime: 5 * 60 * 1000,
  });
}

export function useCampaignLinks(campaignId: string) {
  return useQuery({
    queryKey: appsflyerKeys.links(campaignId),
    queryFn: () => api.listLinks(campaignId),
    enabled: !!campaignId,
  });
}

export function useCampaignReport(campaignId: string, from?: string, to?: string) {
  return useQuery({
    queryKey: appsflyerKeys.report(campaignId, { from, to }),
    queryFn: () => api.getReport(campaignId, from, to),
    enabled: !!campaignId,
  });
}

export function useCampaignSeries(campaignId: string, from?: string, to?: string) {
  return useQuery({
    queryKey: appsflyerKeys.series(campaignId, { from, to }),
    queryFn: () => api.getSeries(campaignId, from, to),
    enabled: !!campaignId,
  });
}

export function useCampaignHealth(campaignId: string) {
  return useQuery({
    queryKey: appsflyerKeys.health(campaignId),
    queryFn: () => api.getHealth(campaignId),
    enabled: !!campaignId,
  });
}

export function useAdminBrandOverview() {
  return useQuery({
    queryKey: appsflyerKeys.adminBrands(),
    queryFn: () => api.getAdminBrandOverview(),
  });
}
